import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { scoutAgentService } from "../../services/scoutAgentService";
import { useDebounce } from "../useDebounce";

// Agent browse feed (design 1h/1i). ALL filters are SERVER params now (M3.5 follow-up
// after the review's N-1): position / country / category / horizon / q, plus the
// age overlap (age_min/age_max), fee-frame kind (fee_frame), and own-agency priority
// (priority) added to the M1 feed endpoint. Pagination is real (server count +
// prev/next) — no client-side refinement over a single page, so the result count and
// the empty state always reflect the TRUE dataset. Agency marks are still fetched and
// merged for the Prio/label PILLS on the cards (display only; the priority FILTER is
// server-side). Mirrors the AbortController + debounce shape of useClubNetworkFeed.
const EMPTY_MARK = { priority: null, labels: [] };

export const useScoutRequestFeed = () => {
  const [rawRequests, setRawRequests] = useState([]);
  const [marksById, setMarksById] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null, currentPage: 1 });

  // Filters (all drive server params)
  const [category, setCategory] = useState(""); // "" | professional | youth
  const [searchTerm, setSearchTerm] = useState("");
  const [position, setPosition] = useState("");
  const [country, setCountry] = useState("");
  const [horizon, setHorizon] = useState("");
  const [ageMin, setAgeMin] = useState("");
  const [ageMax, setAgeMax] = useState("");
  const [feeFrameKind, setFeeFrameKind] = useState(""); // "" | open | free_agents | range
  const [priority, setPriority] = useState(""); // "" | 1 | 2 | 3

  const debouncedSearch = useDebounce(searchTerm, 500);
  const abortRef = useRef(null);
  // Accumulates every club country seen so the Country dropdown stays full even
  // while a country filter is active (which otherwise shrinks the loaded page).
  const seenCountriesRef = useRef(new Set());
  const [countryOptions, setCountryOptions] = useState([]);

  const fetchMarks = useCallback(async () => {
    const res = await scoutAgentService.getMarks();
    if (res.success) {
      const map = {};
      res.data.forEach((m) => {
        map[m.request] = { priority: m.priority ?? null, labels: m.labels || [] };
      });
      setMarksById(map);
    }
  }, []);

  const buildParams = useCallback(
    (page) => {
      const p = new URLSearchParams();
      p.append("page", page);
      if (category) p.append("category", category);
      if (position) p.append("position", position);
      if (country) p.append("country", country);
      if (horizon) p.append("horizon", horizon);
      if (debouncedSearch) p.append("q", debouncedSearch);
      if (ageMin !== "") p.append("age_min", ageMin);
      if (ageMax !== "") p.append("age_max", ageMax);
      if (feeFrameKind) p.append("fee_frame", feeFrameKind);
      if (priority) p.append("priority", priority);
      return p;
    },
    [category, position, country, horizon, debouncedSearch, ageMin, ageMax, feeFrameKind, priority],
  );

  const fetchFeed = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await scoutAgentService.getFeed(buildParams(page), controller.signal);
        if (res.success) {
          setRawRequests(res.data);
          setPagination(res.pagination);
          res.data.forEach((r) => {
            const c = r.club?.country;
            if (c) seenCountriesRef.current.add(c);
          });
          setCountryOptions(Array.from(seenCountriesRef.current).sort());
        } else {
          setError(res.error || "Failed to load scout requests.");
        }
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setError("Failed to load scout requests. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [buildParams],
  );

  // Any filter change rebuilds buildParams → refetch page 1.
  useEffect(() => {
    fetchFeed(1);
  }, [fetchFeed]);

  useEffect(() => {
    fetchMarks();
  }, [fetchMarks]);

  // Merge each request with the agency's mark (priority + labels) for the pills.
  const requests = useMemo(
    () => rawRequests.map((r) => ({ ...r, mark: marksById[r.id] || EMPTY_MARK })),
    [rawRequests, marksById],
  );

  // Optimistic local mark update after the priority/label menu commits, so the card
  // repaints without a full refetch (the server is the source of truth on reload).
  const applyMark = useCallback((requestId, mark) => {
    setMarksById((prev) => ({
      ...prev,
      [requestId]: { priority: mark.priority ?? null, labels: mark.labels || [] },
    }));
  }, []);

  const activeFilters = useMemo(() => {
    const chips = [];
    if (position) chips.push({ key: "position", value: position });
    if (country) chips.push({ key: "country", value: country });
    if (category) chips.push({ key: "category", value: category });
    if (horizon) chips.push({ key: "horizon", value: horizon });
    if (ageMin !== "" || ageMax !== "") chips.push({ key: "age", value: `${ageMin || "0"}–${ageMax || "∞"}` });
    if (feeFrameKind) chips.push({ key: "fee", value: feeFrameKind });
    if (priority) chips.push({ key: "priority", value: `Priority ${priority}` });
    return chips;
  }, [position, country, category, horizon, ageMin, ageMax, feeFrameKind, priority]);

  const clearFilter = useCallback((key) => {
    if (key === "position") setPosition("");
    else if (key === "country") setCountry("");
    else if (key === "category") setCategory("");
    else if (key === "horizon") setHorizon("");
    else if (key === "age") { setAgeMin(""); setAgeMax(""); }
    else if (key === "fee") setFeeFrameKind("");
    else if (key === "priority") setPriority("");
  }, []);

  const clearAll = useCallback(() => {
    setPosition(""); setCountry(""); setCategory(""); setHorizon("");
    setAgeMin(""); setAgeMax(""); setFeeFrameKind(""); setPriority(""); setSearchTerm("");
  }, []);

  return {
    requests,
    loading,
    error,
    pagination,
    fetchFeed,
    // filters
    category, setCategory,
    searchTerm, setSearchTerm,
    position, setPosition,
    country, setCountry,
    horizon, setHorizon,
    ageMin, setAgeMin, ageMax, setAgeMax,
    feeFrameKind, setFeeFrameKind,
    priority, setPriority,
    countryOptions,
    activeFilters, clearFilter, clearAll,
    // marks
    marksById, applyMark, refreshMarks: fetchMarks,
  };
};

export default useScoutRequestFeed;
